import { useFormatDate } from '@ben/common/date';
import { sanitize } from '@ben/common/dom';
import type { ReactNode } from 'react';
import { memo, useMemo } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { TypographyVariant } from '../../utils/theme';
import { formatCurrency } from '../../utils/currency';
import Typography from '../typography/typography';

export interface FormValueProps {
  label?: string;
  date?: string;
  currency?: string;
  value?: ReactNode;
  html?: string;
  children?: ReactNode;
  className?: string;
}

const FormValue = ({
  label,
  date,
  currency,
  value,
  html,
  children,
  className,
}: FormValueProps) => {
  const theme = useTheme();
  const formatDate = useFormatDate();
  const formattedValue = useMemo(() => {
    if (currency && value !== '-') {
      return formatCurrency(value as number, currency);
    }
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    return value;
  }, [currency, value]);

  const sanitizedLabel = label ? sanitize(label) : '';
  return (
    <FormValueWrapper className={className}>
      <Typography
        variant={TypographyVariant.bodySm2}
        color={theme.palette.neutral[90]}
        as="dt"
        dangerouslySetInnerHTML={{ __html: sanitizedLabel }}
      />
      {html && (
        <Typography
          className="ql-editor"
          variant={TypographyVariant.bodyMd1}
          dangerouslySetInnerHTML={{ __html: sanitize(html) }}
          style={{ padding: 0 }} // Remove default ql-editor padding
          as="dd"
        />
      )}
      {value && (
        <Typography
          variant={TypographyVariant.bodyMd1}
          color={theme.palette.neutral[100]}
          as="dd"
        >
          {formattedValue}
        </Typography>
      )}
      {date && (
        <Typography
          variant={TypographyVariant.bodyMd1}
          color={theme.palette.neutral[100]}
          as="dd"
        >
          {formatDate(date)}
        </Typography>
      )}
      {children}
    </FormValueWrapper>
  );
};

export default memo(FormValue);

const FormValueWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};

    img {
      max-width: 100%;
      height: auto;
    }

    p:empty::before {
      content: '\\00a0';
    }
  `}
`;
